import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Award } from "lucide-react";

export default function AdminConfig() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/config").then(({ data }) => setCfg({ ...data, badge_bonuses: data.badge_bonuses || [] })); }, []);
  if (!cfg) return <div className="text-[var(--ink-soft)]">A carregar…</div>;

  const setBadge = (i, key, value) => {
    const next = [...cfg.badge_bonuses];
    next[i] = { ...next[i], [key]: value };
    setCfg({ ...cfg, badge_bonuses: next });
  };
  const addBadge = () => setCfg({ ...cfg, badge_bonuses: [...cfg.badge_bonuses, { threshold: 5, amount: 25, label: "" }] });
  const removeBadge = (i) => setCfg({ ...cfg, badge_bonuses: cfg.badge_bonuses.filter((_, idx) => idx !== i) });

  const save = async () => {
    setBusy(true);
    try {
      const cleanBadges = (cfg.badge_bonuses || [])
        .map((b) => ({
          threshold: Number(b.threshold) || 0,
          amount: Number(b.amount) || 0,
          label: (b.label || "").trim() || null,
        }))
        .filter((b) => b.threshold > 0 && b.amount > 0)
        .sort((a, b) => a.threshold - b.threshold);
      const body = {
        bonus_amount: Number(cfg.bonus_amount),
        min_validation_days: Number(cfg.min_validation_days),
        protocol_version: cfg.protocol_version,
        protocol_text: cfg.protocol_text,
        recruitment_email: cfg.recruitment_email,
        badge_bonuses: cleanBadges,
        share_message_template: cfg.share_message_template,
      };
      const { data } = await api.patch("/config", body);
      setCfg({ ...data, badge_bonuses: data.badge_bonuses || [] });
      toast.success("Definições guardadas");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)]";

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <div className="eyebrow">Administração</div>
        <h1 className="font-heading text-5xl mt-2">Definições do programa</h1>
      </div>

      <div className="card-surface p-6 space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium">Valor do prémio (€)</label>
            <input type="number" step="0.01" value={cfg.bonus_amount} onChange={(e) => setCfg({ ...cfg, bonus_amount: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="cfg-bonus-input"/>
          </div>
          <div>
            <label className="text-sm font-medium">Período mínimo (dias)</label>
            <input type="number" value={cfg.min_validation_days} onChange={(e) => setCfg({ ...cfg, min_validation_days: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="cfg-period-input"/>
          </div>
          <div>
            <label className="text-sm font-medium">Versão do protocolo</label>
            <input value={cfg.protocol_version} onChange={(e) => setCfg({ ...cfg, protocol_version: e.target.value })} className={`mt-1.5 ${inputCls}`}/>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Email de recrutamento</label>
          <input type="email" value={cfg.recruitment_email || ""} onChange={(e) => setCfg({ ...cfg, recruitment_email: e.target.value })} className={`mt-1.5 ${inputCls}`} placeholder="workforce@caring.pt" data-testid="cfg-recruitment-email-input"/>
          <div className="text-[10px] text-[var(--ink-soft)] mt-1">Cada nova candidatura é enviada por email para este endereço.</div>
        </div>

        <div>
          <label className="text-sm font-medium">Mensagem de partilha (WhatsApp/SMS)</label>
          <textarea
            value={cfg.share_message_template || ""}
            onChange={(e) => setCfg({ ...cfg, share_message_template: e.target.value })}
            className={`mt-1.5 ${inputCls} min-h-[180px] font-mono text-xs`}
            data-testid="cfg-share-template-input"
          />
          <div className="text-[10px] text-[var(--ink-soft)] mt-1">
            Tokens disponíveis: <code>{"{locality}"}</code>, <code>{"{service_line}"}</code>, <code>{"{price_line}"}</code>, <code>{"{schedule}"}</code>, <code>{"{apply_url}"}</code>. Use <code>*negrito*</code> para formatação WhatsApp.
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Texto do protocolo</label>
          <textarea value={cfg.protocol_text} onChange={(e) => setCfg({ ...cfg, protocol_text: e.target.value })} className={`mt-1.5 ${inputCls} min-h-[300px] font-mono text-sm`} data-testid="cfg-protocol-input"/>
        </div>

        <button onClick={save} disabled={busy} className="pill-btn pill-primary" data-testid="cfg-save-btn"><Save className="w-4 h-4"/> {busy ? "A guardar…" : "Guardar definições"}</button>
      </div>

      <div className="card-surface p-6 space-y-4" data-testid="badge-bonuses-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="eyebrow flex items-center gap-1.5"><Award className="w-3.5 h-3.5"/> Prémios por emblema</div>
            <h2 className="font-heading text-2xl mt-1">Escalões automáticos</h2>
            <p className="text-xs text-[var(--ink-soft)] mt-1 max-w-lg">Sempre que um referenciador atinge o número de contratações validadas indicado, o sistema cria automaticamente um prémio extra em Cartão Dá. Cada escalão é atribuído apenas uma vez por referenciador.</p>
          </div>
          <button type="button" onClick={addBadge} className="pill-btn pill-ghost !py-2 !px-3" data-testid="badge-add-btn"><Plus className="w-4 h-4"/> Adicionar escalão</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="text-left text-[var(--ink-soft)] text-xs">
                <th className="pb-2 pr-3">Contratações mínimas</th>
                <th className="pb-2 pr-3">Prémio extra (€)</th>
                <th className="pb-2 pr-3">Etiqueta</th>
                <th className="pb-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {(cfg.badge_bonuses || []).map((b, i) => (
                <tr key={i} className="border-t border-[var(--border)]" data-testid={`badge-row-${i}`}>
                  <td className="py-2 pr-3">
                    <input type="number" min="1" value={b.threshold} onChange={(e) => setBadge(i, "threshold", e.target.value)} className={`${inputCls} w-28`} data-testid={`badge-threshold-${i}`}/>
                  </td>
                  <td className="py-2 pr-3">
                    <input type="number" min="0" step="0.01" value={b.amount} onChange={(e) => setBadge(i, "amount", e.target.value)} className={`${inputCls} w-28`} data-testid={`badge-amount-${i}`}/>
                  </td>
                  <td className="py-2 pr-3">
                    <input value={b.label || ""} onChange={(e) => setBadge(i, "label", e.target.value)} placeholder="Bronze / Prata / Ouro" className={`${inputCls} w-full`} data-testid={`badge-label-${i}`}/>
                  </td>
                  <td className="py-2 text-right">
                    <button type="button" onClick={() => removeBadge(i)} className="pill-btn pill-ghost !py-1.5 !px-3 !text-[var(--terracotta)]" data-testid={`badge-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </td>
                </tr>
              ))}
              {(!cfg.badge_bonuses || cfg.badge_bonuses.length === 0) && (
                <tr><td colSpan={4} className="py-6 text-center text-[var(--ink-soft)]">Nenhum escalão configurado. Adicione o primeiro acima.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-[var(--ink-soft)]">Não esqueça de clicar em "Guardar definições" no painel acima para persistir as alterações.</div>
      </div>
    </div>
  );
}
